import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import { jsPDF } from "jspdf";
import "react-datepicker/dist/react-datepicker.css";
import Navbar from "../../components/Navbar";
import { roomsAPI, bookingsAPI, teamsAPI } from "../../services/meetingsAPI";
import { getImageUrl } from "../../utils/imageHelper";
import { toast } from "react-toastify";
import { persistPendingRange } from "../../utils/schedulePendingStorage";
import "./BookRoomFlow.css";

const STEPS = [
  { id: 1, title: "Basic Info", subtitle: "User Details" },
  { id: 2, title: "Property", subtitle: "Meeting Room Details" },
  { id: 3, title: "Check-in / Check-out", subtitle: "Booking Time" },
  { id: 4, title: "Pricing", subtitle: "Price Breakdown" },
  { id: 5, title: "Payment", subtitle: "Payment Method" },
  { id: 6, title: "Confirmation", subtitle: "Booking Summary" },
];

const TIME_SLOTS = Array.from({ length: 48 }, (_, index) => {
  const totalMinutes = index * 30;
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
});

const getNextTimeSlot = (timeKey) => {
  const index = TIME_SLOTS.indexOf(timeKey);
  if (index >= 0 && index < TIME_SLOTS.length - 1) {
    return TIME_SLOTS[index + 1];
  }
  return "";
};

const parseDateFromUrl = (ymd) => {
  if (!ymd) return new Date();
  const [year, month, day] = ymd.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const PAYMENT_METHODS = [
  { id: "upi", label: "UPI", icon: "UPI" },
  { id: "card", label: "Credit / Debit Card", icon: "CARD" },
  { id: "netbanking", label: "Net Banking", icon: "BANK" },
  { id: "wallet", label: "Wallet", icon: "WALLET" },
];

const AMENITY_LABELS = { Projector: "Projector", WiFi: "WiFi", Whiteboard: "Whiteboard", AC: "AC" };
const DAY_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const OFFICE_ROOM_ID_PATTERN = /^office-([0-9a-fA-F]{24})(?:-(\d+))?$/;
const MONGO_ID_PATTERN = /^[0-9a-fA-F]{24}$/;
const COMPANY_PROFILE_ROLES = new Set(["company_admin", "company_manager", "company_staff"]);
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const resolveImageUrl = (img) => {
  if (!img) return null;
  if (typeof img === "string" && (img.startsWith("http") || img.startsWith("data:"))) {
    return img;
  }
  return getImageUrl(img);
};

const normalizeRoomForFlow = (rawRoom) => {
  if (!rawRoom) return null;
  return {
    id: rawRoom._id || rawRoom.id || "",
    _id: rawRoom._id || rawRoom.id || "",
    userId: rawRoom.userId || null,
    ownerId:
      rawRoom.ownerId ||
      (typeof rawRoom.userId === "object"
        ? rawRoom.userId?._id || rawRoom.userId?.id
        : rawRoom.userId) ||
      "",
    hostEmail: rawRoom.hostEmail || rawRoom.ownerEmail || rawRoom.userId?.email || "",
    ownerEmail: rawRoom.ownerEmail || rawRoom.hostEmail || rawRoom.userId?.email || "",
    roomName: rawRoom.name || rawRoom.roomName || "Meeting Room",
    name: rawRoom.name || rawRoom.roomName || "Meeting Room",
    location: rawRoom.location?.city || rawRoom.city || rawRoom.location || "-",
    city: rawRoom.location?.city || rawRoom.city || "",
    capacity: rawRoom.capacity ?? 0,
    amenities: rawRoom.amenities || [],
    description: rawRoom.description || "",
    services: rawRoom.services || "",
    surroundings: rawRoom.surroundings || "",
    comments: rawRoom.comments || "",
    rules: rawRoom.rules || "",
    policy: rawRoom.policy || "",
    availability: rawRoom.availability || null,
    pricePerHour: rawRoom.pricePerHour ?? 0,
    cleaningResponsibility: rawRoom?.cleaningResponsibility || "member_clean",
    cleaningCharge: rawRoom?.cleaningCharge ?? 0,
    images: (rawRoom.images || []).map(resolveImageUrl).filter(Boolean),
  };
};

const formatAvailabilityRows = (availability) => {
  if (!availability || typeof availability !== "object" || Array.isArray(availability)) {
    return [];
  }

  return Object.keys(DAY_LABELS).map((dayKey) => {
    const slot = availability[dayKey];
    if (!slot || slot.available === false) {
      return `${DAY_LABELS[dayKey]}: Not available`;
    }

    const startTime = slot.startTime || "--:--";
    const endTime = slot.endTime || "--:--";
    return `${DAY_LABELS[dayKey]}: ${startTime} - ${endTime}`;
  });
};

const BookRoomFlow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomIdFromUrl = searchParams.get("roomId");
  const dateFromUrl = searchParams.get("date");
  const timeFromUrl = searchParams.get("time");
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [room, setRoom] = useState(null);
  const [roomLoading, setRoomLoading] = useState(true);
  const [roomBookings, setRoomBookings] = useState([]);

  // Step 1: Basic Info
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [numberOfAttendees, setNumberOfAttendees] = useState(2);
  const [additionalMembers, setAdditionalMembers] = useState([]);

  // Auto-fill Basic Info from logged-in user
  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const user = JSON.parse(stored);
        if (user.name)  setFullName(user.name);
        if (user.email) setEmail(user.email);
        if (user.phone || user.phoneNumber) setPhone(user.phone || user.phoneNumber);

        // Auto-fill company name from the logged-in user's company.
        const company = user.company;
        let derivedCompanyName = "";
        if (company && typeof company === "object") {
          derivedCompanyName =
            company.profile?.displayName ||
            company.displayName ||
            company.name ||
            "";
        } else if (typeof company === "string") {
          derivedCompanyName = company;
        }
        if (!derivedCompanyName) {
          derivedCompanyName = user.companyName || user.company_display_name || "";
        }
        if (derivedCompanyName) setCompanyName(derivedCompanyName);
      }
    } catch (_) {}
  }, []);

  // Best-effort fetch from company profile API in case the localStorage user
  // doesn't carry a populated company object.
  useEffect(() => {
    let cancelled = false;
    const fetchCompany = async () => {
      try {
        if (companyName) return;
        const token = localStorage.getItem("token");
        if (!token) return;
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        const role = String(storedUser?.role || "");
        const hasCompanyRole = COMPANY_PROFILE_ROLES.has(role);
        if (!hasCompanyRole) return;
        const { companyProfileAPI } = await import("../../services/meetingsAPI");
        if (!companyProfileAPI?.getMyCompanyProfile) return;
        const response = await companyProfileAPI.getMyCompanyProfile();
        if (cancelled) return;
        const payload = response?.data?.data || response?.data || {};
        const name =
          payload.profile?.displayName ||
          payload.displayName ||
          payload.name ||
          "";
        if (name) setCompanyName(name);
      } catch (_) {
        // silent — field is optional
      }
    };
    fetchCompany();
    return () => {
      cancelled = true;
    };
    // Only run once on mount; companyName changes are user-driven afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 3: Check-in / Check-out
  const [selectedDate, setSelectedDate] = useState(() => parseDateFromUrl(dateFromUrl));
  const [startTime, setStartTime] = useState(timeFromUrl || "");
  const [endTime, setEndTime] = useState(() => getNextTimeSlot(timeFromUrl || ""));

  // Step 5: Payment
  const [paymentType, setPaymentType] = useState(""); // 'online' | 'cash'
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDetails, setPaymentDetails] = useState("");
  const [bankSearch, setBankSearch] = useState("");
  const [showConfirmingPopup, setShowConfirmingPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [paymentRef, setPaymentRef] = useState("");

  // Step 6: Confirmation
  const [bookingId, setBookingId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bookingDone, setBookingDone] = useState(false);

    // Fetch room when roomId present
  useEffect(() => {
    if (!roomIdFromUrl) {
      setRoomLoading(false);
      return;
    }

    let cancelled = false;
    setRoomLoading(true);

    const matchRoomFromList = (rooms, targetId, officeMatch) => {
      let apiMatched = rooms.find((r) => String(r._id || r.id || "") === targetId);
      if (!apiMatched && officeMatch) {
        const officeId = officeMatch[1];
        const roomIndex = officeMatch[2] != null ? parseInt(officeMatch[2], 10) : 0;
        const officeRooms = rooms.filter(
          (r) => r.isOfficeRoom && String(r.officeRoomId || "") === String(officeId)
        );
        apiMatched = officeRooms[roomIndex] || officeRooms[0] || null;
      }
      return apiMatched ? normalizeRoomForFlow(apiMatched) : null;
    };

    const fetchRoom = async () => {
      try {
        const roomId = decodeURIComponent(String(roomIdFromUrl || "")).trim();
        const officeMatch = roomId.match(OFFICE_ROOM_ID_PATTERN);
        let resolvedRoom = null;

        if (MONGO_ID_PATTERN.test(roomId)) {
          try {
            const res = await roomsAPI.getRoom(roomId);
            resolvedRoom = normalizeRoomForFlow(res.data);
          } catch (_) {
            // fallback to list lookup below
          }
        }

        if (!resolvedRoom) {
          try {
            const res = await roomsAPI.getRooms({
              sort: "latest",
              ownerAddedOnly: true,
              managedOnly: true,
            });
            const rooms = Array.isArray(res?.data) ? res.data : [];
            resolvedRoom = matchRoomFromList(rooms, roomId, officeMatch);
          } catch (_) {
            // no-op
          }
        }

        if (!resolvedRoom) {
          try {
            const res = await roomsAPI.getRooms({ sort: "latest", ownerAddedOnly: true });
            const rooms = Array.isArray(res?.data) ? res.data : [];
            resolvedRoom = matchRoomFromList(rooms, roomId, officeMatch);
          } catch (_) {
            // no-op
          }
        }

        if (!resolvedRoom) {
          try {
            const localRooms = JSON.parse(localStorage.getItem("meetingRooms") || "[]");
            const local = localRooms.find(
              (r) => String(r.id || r._id || "") === roomId
            );
            if (local) {
              resolvedRoom = normalizeRoomForFlow({
                ...local,
                _id: local._id || local.id,
                name: local.name || local.roomName,
              });
            }
          } catch (_) {
            // no-op
          }
        }

        if (!cancelled) {
          setRoom(resolvedRoom);
        }
      } catch (e) {
        if (!cancelled) setRoom(null);
      } finally {
        if (!cancelled) setRoomLoading(false);
      }
    };

    fetchRoom();
    return () => {
      cancelled = true;
    };
  }, [roomIdFromUrl]);

  // Fetch room bookings for selected date
  const fetchRoomBookings = useCallback(async () => {
    if (!room?.id || !selectedDate) return;
    try {
      const dateStr = selectedDate.toISOString().split("T")[0];
      const response = await bookingsAPI.getRoomBookings(room.id, dateStr);
      setRoomBookings(response.data || []);
    } catch (_) {
      setRoomBookings([]);
    }
  }, [room?.id, selectedDate]);

  useEffect(() => {
    fetchRoomBookings();
  }, [fetchRoomBookings]);

  const SLOT_DURATION_MINUTES = 30;
  const MAX_BOOKING_MINUTES = 240;

  const toSlotDateTime = (time) => {
    if (!time) return null;
    const [hours, minutes] = String(time).split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    const slotDate = new Date(selectedDate);
    slotDate.setHours(hours, minutes, 0, 0);
    return slotDate;
  };

  const isTimeSlotBooked = (time) => {
    const slotStart = toSlotDateTime(time);
    if (!slotStart) return true;
    const slotEnd = new Date(slotStart.getTime() + SLOT_DURATION_MINUTES * 60 * 1000);
    return roomBookings.some((b) => {
      const start = new Date(b.startTime);
      const end = new Date(b.endTime);
      return slotStart < end && slotEnd > start;
    });
  };

  const checkTimeConflict = (start, end) => {
    const startD = toSlotDateTime(start);
    const endD = toSlotDateTime(end);
    if (!startD || !endD || endD <= startD) return true;
    return roomBookings.some((b) => {
      const bs = new Date(b.startTime);
      const be = new Date(b.endTime);
      return startD < be && endD > bs;
    });
  };

  const getAvailableStartSlots = () => {
    const now = new Date();
    const today = new Date(selectedDate);
    today.setHours(0, 0, 0, 0);
    const todayNow = new Date();
    todayNow.setHours(0, 0, 0, 0);
    if (today.getTime() !== todayNow.getTime()) return TIME_SLOTS;
    return TIME_SLOTS.filter((time) => {
      const d = toSlotDateTime(time);
      if (!d) return false;
      return d >= now;
    });
  };

  const getEndTimeSlotsForStart = (startValue = startTime) => {
    if (!startValue) return [];
    const startIdx = TIME_SLOTS.indexOf(startValue);
    if (startIdx === -1) return [];

    return TIME_SLOTS
      .slice(startIdx + 1)
      .filter((candidateEnd) => {
        const startDate = toSlotDateTime(startValue);
        const endDate = toSlotDateTime(candidateEnd);
        if (!startDate || !endDate || endDate <= startDate) return false;
        const durationMinutes = (endDate - startDate) / (1000 * 60);
        if (durationMinutes < SLOT_DURATION_MINUTES || durationMinutes > MAX_BOOKING_MINUTES) {
          return false;
        }
        return !checkTimeConflict(startValue, candidateEnd);
      });
  };

  const canStartSlotBeBooked = (time) => {
    if (isTimeSlotBooked(time)) return false;
    return getEndTimeSlotsForStart(time).length > 0;
  };

  useEffect(() => {
    if (!selectedDate || Number.isNaN(new Date(selectedDate).getTime())) return;

    const selectedDateStart = new Date(selectedDate);
    selectedDateStart.setHours(0, 0, 0, 0);
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    if (selectedDateStart.getTime() === todayStart.getTime()) {
      const available = getAvailableStartSlots().filter((time) =>
        canStartSlotBeBooked(time)
      );
      if (available.length === 0) {
        const tomorrow = new Date(selectedDateStart);
        tomorrow.setDate(tomorrow.getDate() + 1);
        setSelectedDate(tomorrow);
        setStartTime("");
        setEndTime("");
        toast.info("Today's slots are over. Showing tomorrow's slots.");
      }
    }
  }, [selectedDate]);

  const getEndTimeSlots = () => {
    return getEndTimeSlotsForStart(startTime);
  };

  const durationHours = (() => {
    if (!startTime || !endTime) return 0;
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    return (eh * 60 + em - sh * 60 - sm) / 60;
  })();

  const pricePerHour = room?.pricePerHour ?? 0;
  const subtotal = pricePerHour * durationHours;
  const gstPercent = 18;
  const gst = (subtotal * gstPercent) / 100;
  const platformFeePercent = 5;
  const platformFee = (subtotal * platformFeePercent) / 100;
  const cleaningCharge = room?.cleaningResponsibility === "owner_clean"
    ? Math.max(0, Number(room?.cleaningCharge) || 0)
    : 0;
  const cleaningResponsibilityLabel = room?.cleaningResponsibility === "owner_clean"
    ? "Owner cleans the property"
    : "Members clean the property";
  const totalAmount = subtotal + gst + platformFee + cleaningCharge;
  const availabilityRows = formatAvailabilityRows(room?.availability);

  const isAvailable = startTime && endTime && !checkTimeConflict(startTime, endTime) && durationHours >= 0.5;
  const normalizedAdditionalMembers = additionalMembers.map((member) => ({
    name: String(member?.name || "").trim(),
    email: String(member?.email || "").trim(),
  }));
  const filledAdditionalMembers = normalizedAdditionalMembers.filter(
    (member) => member.name || member.email
  );
  const areAdditionalMembersValid = filledAdditionalMembers.every(
    (member) => member.name && member.email && EMAIL_REGEX.test(member.email)
  );

  const canProceedStep1 =
    fullName.trim() &&
    email.trim() &&
    phone.trim() &&
    numberOfAttendees >= 1 &&
    areAdditionalMembersValid;
  const canProceedStep3 = selectedDate && startTime && endTime && durationHours >= 0.5 && isAvailable;

  const handleAddMember = () => {
    const hasIncompleteMember = filledAdditionalMembers.some(
      (member) => !member.name || !member.email || !EMAIL_REGEX.test(member.email)
    );
    if (hasIncompleteMember) {
      toast.error("Please complete existing member details first.");
      return;
    }

    const hasEmptyRow = normalizedAdditionalMembers.some(
      (member) => !member.name && !member.email
    );
    if (hasEmptyRow) return;

    setAdditionalMembers((prev) => [...prev, { name: "", email: "" }]);
  };

  const handleMemberChange = (index, field, value) => {
    setAdditionalMembers((prev) =>
      prev.map((member, idx) => (idx === index ? { ...member, [field]: value } : member))
    );
  };

  const handleRemoveMember = (index) => {
    setAdditionalMembers((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleNext = () => {
    if (currentStep < 6) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleDownloadInvoicePdf = () => {
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const marginLeft = 48;
      const pageWidth = doc.internal.pageSize.getWidth();
      const rightColumnX = pageWidth - marginLeft;
      const safeBookingId = String(bookingId || `BR-${Date.now()}`);
      const bookingDate = selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())
        ? selectedDate.toLocaleDateString("en-IN")
        : "-";
      const bookingDateTime = `${bookingDate} ${startTime || "-"} - ${endTime || "-"}`;
      const paymentLabel = paymentMethod ? paymentMethod.toUpperCase() : (paymentType ? paymentType.toUpperCase() : "N/A");
      const createdAt = new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      let y = 56;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("UBSAAPP", marginLeft, y);
      doc.setFontSize(13);
      doc.text("Meeting Booking Receipt", marginLeft, y + 22);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Generated: ${createdAt}`, rightColumnX, y + 5, { align: "right" });
      doc.text(`Receipt ID: ${safeBookingId}`, rightColumnX, y + 20, { align: "right" });

      y += 44;
      doc.setDrawColor(220, 220, 220);
      doc.line(marginLeft, y, rightColumnX, y);
      y += 28;

      const drawField = (label, value) => {
        const textValue = String(value ?? "-");
        const wrappedValue = doc.splitTextToSize(textValue, pageWidth - 240);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(`${label}:`, marginLeft, y);
        doc.setFont("helvetica", "normal");
        doc.text(wrappedValue, marginLeft + 130, y);
        y += Math.max(22, wrappedValue.length * 15);
      };

      drawField("Booking ID", safeBookingId);
      drawField("Guest Name", fullName || currentUser?.name || "-");
      drawField("Email", email || currentUser?.email || "-");
      drawField("Phone", phone || currentUser?.phoneNumber || "-");
      drawField("Room Name", room?.roomName || room?.name || "Meeting Room");
      drawField("Location", room?.location || room?.city || "-");
      drawField("Date & Time", bookingDateTime);
      drawField("Duration", `${durationHours.toFixed(1)} hour(s)`);
      drawField("Attendees", numberOfAttendees || "-");
      drawField("Payment Method", paymentLabel);
      drawField(
        "Cleaning",
        `${cleaningResponsibilityLabel}${cleaningCharge > 0 ? ` (Rs ${cleaningCharge.toFixed(2)})` : " (Rs 0.00)"}`
      );

      y += 10;
      doc.setDrawColor(220, 220, 220);
      doc.line(marginLeft, y, rightColumnX, y);
      y += 24;

      drawField("Room Subtotal", `Rs ${subtotal.toFixed(2)}`);
      drawField("GST", `Rs ${gst.toFixed(2)} (${gstPercent}%)`);
      drawField("Platform Fee", `Rs ${platformFee.toFixed(2)} (${platformFeePercent}%)`);
      drawField("Total Paid Amount", `Rs ${totalAmount.toFixed(2)}`);
      drawField("Booking Status", "Confirmed");

      y += 16;
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      doc.text("Thank you for booking with UBSAAPP.", marginLeft, y);

      const fileSafeId = safeBookingId.replace(/[^a-zA-Z0-9_-]/g, "");
      doc.save(`meeting-receipt-${fileSafeId || "booking"}.pdf`);
    } catch (error) {
      toast.error("Unable to download receipt PDF. Please try again.");
    }
  };

  const handlePaymentSubmit = async () => {
    if (!room?.id || !startTime || !endTime || !fullName.trim() || !email.trim()) {
      toast.error("Please complete Basic Info and Check-in steps.");
      return;
    }
    if (!isAvailable) {
      toast.error("Selected time slot is not available.");
      return;
    }

    // Netbanking: show confirming popup first, then success
    if (paymentMethod === 'netbanking' && paymentDetails) {
      setShowConfirmingPopup(true);
      setTimeout(async () => {
        setShowConfirmingPopup(false);
        // Do actual booking
        await doBooking();
        const ref = `pay_${Math.random().toString(36).substr(2, 12).toUpperCase()}`;
        setPaymentRef(ref);
        setShowSuccessPopup(true);
      }, 2500);
      return;
    }

    await doBooking();
  };

  const doBooking = async () => {
    setSubmitting(true);
    try {
      const start = new Date(selectedDate);
      const [sh, sm] = startTime.split(":").map(Number);
      start.setHours(sh, sm, 0, 0);
      const end = new Date(selectedDate);
      const [eh, em] = endTime.split(":").map(Number);
      end.setHours(eh, em, 0, 0);

      let teamId = null;
      try {
        const teamMembers = [
          { name: fullName.trim(), email: email.trim() },
          ...filledAdditionalMembers,
        ];
        const res = await teamsAPI.createTeam({
          teamName: companyName.trim() || "Booking Team",
          members: teamMembers,
        });
        const newTeam = res.data?.data ?? res.data;
        teamId = newTeam?._id;
      } catch (e) {
        const myTeams = await teamsAPI.getMyTeams();
        const teams = myTeams.data || [];
        if (teams.length > 0) teamId = teams[0]._id;
      }
      if (!teamId) {
        toast.error("Could not create or find a team for booking.");
        setSubmitting(false);
        return;
      }

      const bookingResponse = await bookingsAPI.createBooking({
        room: room.id,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        purpose: "Meeting",
        team: teamId,
        selectedMembers: filledAdditionalMembers,
        attendees: numberOfAttendees,
        notes: `Booker: ${fullName}, ${email}, ${phone}${companyName ? `, ${companyName}` : ""}. Payment: ${paymentMethod || "N/A"}. Cleaning: ${cleaningResponsibilityLabel}${cleaningCharge ? ` (Rs ${cleaningCharge.toFixed(2)})` : " (Rs 0)"}.`,
      });

      const createdBooking = bookingResponse?.data;
      const createdStatus = String(createdBooking?.status || "").toLowerCase();
      if (createdStatus === "pending") {
        const dateYmd = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;
        persistPendingRange(room.id, start, end);
        sessionStorage.setItem(
          "schedulePendingHold",
          JSON.stringify({
            roomId: String(room.id),
            date: dateYmd,
            time: startTime,
          })
        );
      } else {
        sessionStorage.removeItem("schedulePendingHold");
      }

      setBookingId(`BR-${Date.now()}`);
      setBookingDone(true);
      setCurrentStep(6);
      toast.success("Booking confirmed!");
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Booking failed.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (roomIdFromUrl && roomLoading) {
    return (
      <div className="book-flow-page">
        <Navbar isScrolled={false} />
        <div className="book-flow-loading">
          <div className="book-flow-spinner" />
          <p>Loading room...</p>
        </div>
      </div>
    );
  }

  if (roomIdFromUrl && !room) {
    return (
      <div className="book-flow-page">
        <Navbar isScrolled={false} />
        <div className="book-flow-error">
          <p>Room not found.</p>
          <button type="button" className="book-flow-btn-primary" onClick={() => navigate("/meeting-home")}>
            Go to Meeting Home
          </button>
        </div>
      </div>
    );
  }

  if (!roomIdFromUrl) {
    return (
      <div className="book-flow-page">
        <Navbar isScrolled={false} />
        <div className="book-flow-error">
          <p>Please select a room to book (e.g. from Meeting Home or Search).</p>
          <button type="button" className="book-flow-btn-primary" onClick={() => navigate("/meeting-home")}>
            Go to Meeting Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="book-flow-page">
      <Navbar isScrolled={false} />
      <div className="book-flow-container">
        <button type="button" className="book-flow-back" onClick={() => navigate(-1)}>
          {"<- Go Back"}
        </button>
        <h1 className="book-flow-main-title">Book Meeting Room</h1>

        {/* Step indicator */}
        <div className="book-flow-steps-bar">
          {STEPS.map((step) => (
            <button
              key={step.id}
              type="button"
              className={`book-flow-step-dot ${currentStep === step.id ? "active" : ""} ${currentStep > step.id ? "done" : ""}`}
              onClick={() => setCurrentStep(step.id)}
              aria-current={currentStep === step.id ? "step" : undefined}
            >
              <span className="book-flow-step-num">{step.id}</span>
              <span className="book-flow-step-label">{step.title}</span>
            </button>
          ))}
        </div>

        <div className="book-flow-card">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <>
              <h2 className="book-flow-step-title">Basic Info</h2>
              <p className="book-flow-step-subtitle">User Details Section</p>
              <div className="book-flow-form">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter full name"
                  required
                />
                <label>Email Address *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email"
                  required
                />
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter phone number"
                  required
                />
                <label>Company Name (Optional)</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Enter company name"
                />
                <label>Number of Attendees *</label>
                <select
                  value={numberOfAttendees}
                  onChange={(e) => setNumberOfAttendees(Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>

                <div className="book-flow-members-section">
                  <div className="book-flow-members-header">
                    <span className="book-flow-members-title">Additional Members</span>
                    <button
                      type="button"
                      className="book-flow-add-member-btn"
                      onClick={handleAddMember}
                    >
                      Add Member
                    </button>
                  </div>

                  {additionalMembers.map((member, index) => (
                    <div key={`member-${index}`} className="book-flow-member-row">
                      <input
                        type="text"
                        value={member.name}
                        onChange={(e) => handleMemberChange(index, "name", e.target.value)}
                        placeholder={`Member ${index + 1} Name`}
                      />
                      <input
                        type="email"
                        value={member.email}
                        onChange={(e) => handleMemberChange(index, "email", e.target.value)}
                        placeholder={`Member ${index + 1} Email`}
                      />
                      <div className="book-flow-member-actions">
                        <button
                          type="button"
                          className="book-flow-inline-add-member-btn"
                          onClick={handleAddMember}
                          aria-label={`Add member near member ${index + 1}`}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className="book-flow-remove-member-btn"
                          onClick={() => handleRemoveMember(index)}
                          aria-label={`Remove member ${index + 1}`}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}

                  {!areAdditionalMembersValid && filledAdditionalMembers.length > 0 && (
                    <p className="book-flow-members-error">
                      Please enter valid name and email for all added members.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Step 2: Property */}
          {currentStep === 2 && (
            <>
              <h2 className="book-flow-step-title">Property</h2>
              <p className="book-flow-step-subtitle">Meeting Room Details</p>
              <div className="book-flow-room-details">
                <div className="book-flow-room-image-wrap">
                  {room.images?.[0] ? (
                    <img src={room.images[0]} alt={room.roomName} className="book-flow-room-img" />
                  ) : (
                    <div className="book-flow-room-placeholder">No image</div>
                  )}
                </div>
                <div className="book-flow-room-meta">
                  <p><strong>Room Name:</strong> {room.roomName}</p>
                  <p><strong>Location:</strong> {room.location}</p>
                  <p><strong>Seating Capacity:</strong> {room.capacity} people</p>
                  <p><strong>Amenities:</strong></p>
                  <ul className="book-flow-amenities">
                    {["Projector", "WiFi", "Whiteboard", "AC"].map((key) => (
                      <li key={key}>
                        {room.amenities?.includes(key) ? "Yes" : "-"} {AMENITY_LABELS[key] || key}
                      </li>
                    ))}
                  </ul>
                  <p><strong>Price:</strong> Rs {room.pricePerHour}/hour</p>
                  <p><strong>Cleaning:</strong> {cleaningResponsibilityLabel} - {cleaningCharge > 0 ? `Rs ${cleaningCharge.toLocaleString()}` : "Rs 0"}</p>
                </div>
              </div>
              <div className="book-flow-room-extra-details">
                <div className="book-flow-room-extra-item">
                  <h4>Description</h4>
                  <p>{room.description || "Not provided"}</p>
                </div>
                <div className="book-flow-room-extra-item">
                  <h4>Services</h4>
                  <p>{room.services || "Not provided"}</p>
                </div>
                <div className="book-flow-room-extra-item">
                  <h4>Surroundings</h4>
                  <p>{room.surroundings || "Not provided"}</p>
                </div>
                <div className="book-flow-room-extra-item">
                  <h4>Comments</h4>
                  <p>{room.comments || "Not provided"}</p>
                </div>
                <div className="book-flow-room-extra-item">
                  <h4>Rules</h4>
                  <p>{room.rules || "Not provided"}</p>
                </div>
                <div className="book-flow-room-extra-item">
                  <h4>Policy</h4>
                  <p>{room.policy || "Not provided"}</p>
                </div>
                <div className="book-flow-room-extra-item">
                  <h4>Availability</h4>
                  {availabilityRows.length > 0 ? (
                    <ul className="book-flow-room-availability-list">
                      {availabilityRows.map((row) => (
                        <li key={row}>{row}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>Not provided</p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Step 3: Check-in / Check-out */}
          {currentStep === 3 && (
            <>
              <h2 className="book-flow-step-title">Check-in / Check-out</h2>
              <p className="book-flow-step-subtitle">Booking Time Selection (Current Active Step)</p>
              <div className="book-flow-form">
                <label>Select Date *</label>
                <DatePicker
                  selected={selectedDate}
                  onChange={(d) => setSelectedDate(d)}
                  minDate={new Date()}
                  className="book-flow-input"
                  dateFormat="dd/MM/yyyy"
                />
                <label>Start Time *</label>
                <select
                  value={startTime}
                  onChange={(e) => {
                    setStartTime(e.target.value);
                    setEndTime("");
                  }}
                  className="book-flow-input"
                >
                  <option value="">Select start time</option>
                  {getAvailableStartSlots().filter((t) => canStartSlotBeBooked(t)).length === 0 && (
                    <option value="" disabled>
                      No slots available for selected date
                    </option>
                  )}
                  {getAvailableStartSlots().map((t) => (
                    <option key={t} value={t} disabled={!canStartSlotBeBooked(t)}>
                      {t} {isTimeSlotBooked(t) ? "(Busy)" : ""}
                    </option>
                  ))}
                </select>
                <label>End Time *</label>
                <select
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="book-flow-input"
                  disabled={!startTime}
                >
                  <option value="">Select end time</option>
                  {startTime && getEndTimeSlots().length === 0 && (
                    <option value="" disabled>
                      No end-time slots available
                    </option>
                  )}
                  {getEndTimeSlots().map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {startTime && endTime && (
                  <>
                    <p className="book-flow-duration">Duration: {durationHours.toFixed(1)} hour(s)</p>
                    <p className={`book-flow-availability ${isAvailable ? "available" : "unavailable"}`}>
                      Availability: {isAvailable ? "Available" : "Not Available"}
                    </p>
                  </>
                )}
              </div>
            </>
          )}

          {/* Step 4: Pricing */}
          {currentStep === 4 && (
            <>
              <h2 className="book-flow-step-title">Pricing</h2>
              <p className="book-flow-step-subtitle">Price Breakdown</p>
              <div className="book-flow-pricing">
                <div className="book-flow-pricing-rows">
                  <div className="book-flow-pricing-row">
                    <span>Room subtotal (Rs {pricePerHour} x {durationHours.toFixed(1)}h)</span>
                    <span>Rs {subtotal.toFixed(2)}</span>
                  </div>
                  <div className="book-flow-pricing-row">
                    <span>GST ({gstPercent}%)</span>
                    <span>Rs {gst.toFixed(2)}</span>
                  </div>
                  <div className="book-flow-pricing-row">
                    <span>Platform Fee ({platformFeePercent}%)</span>
                    <span>Rs {platformFee.toFixed(2)}</span>
                  </div>
                  <div className="book-flow-pricing-row">
                    <span>Cleaning ({cleaningResponsibilityLabel})</span>
                    <span>{cleaningCharge > 0 ? `Rs ${cleaningCharge.toFixed(2)}` : "Rs 0"}</span>
                  </div>
                </div>
                <p className="book-flow-total">Total Amount: Rs {totalAmount.toFixed(2)}</p>
              </div>
            </>
          )}

          {/* Step 5: Payment */}
          {currentStep === 5 && (
            <>
              <h2 className="book-flow-step-title">Payment</h2>
              <p className="book-flow-step-subtitle">Select Payment Method</p>

              {/* Payment type selector */}
              <div className="brf-pay-type-row">
                <button
                  type="button"
                  className={`brf-pay-type-btn${paymentType === 'online' ? ' brf-pay-type-btn--active' : ''}`}
                  onClick={() => { setPaymentType('online'); setPaymentMethod('card'); setPaymentDetails(''); }}
                >
                  <span className="brf-pay-type-icon">CARD</span>
                  <span className="brf-pay-type-label">Online Payment</span>
                  <span className="brf-pay-type-sub">UPI, Card, Net Banking, Wallet</span>
                </button>
                <button
                  type="button"
                  className={`brf-pay-type-btn${paymentType === 'cash' ? ' brf-pay-type-btn--active' : ''}`}
                  onClick={() => { setPaymentType('cash'); setPaymentMethod('cash'); setPaymentDetails(''); }}
                >
                  <span className="brf-pay-type-icon">CASH</span>
                  <span className="brf-pay-type-label">Cash Payment</span>
                  <span className="brf-pay-type-sub">Pay at the venue</span>
                </button>
              </div>

              {/* Razorpay-style online payment panel */}
              {paymentType === 'online' && (
                <div className="brf-razorpay-wrap">
                  {/* Left: method list */}
                  <div className="brf-rp-left">
                    {[
                      { id: 'card',       label: 'Cards',       icons: ['CARD','ATM'] },
                      { id: 'upi',        label: 'UPI',         icons: ['UPI','PAY'] },
                      { id: 'netbanking', label: 'Netbanking',  icons: ['BANK','SECURE'] },
                      { id: 'wallet',     label: 'Wallet',      icons: ['WALLET','MONEY'] },
                    ].map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        className={`brf-rp-method${paymentMethod === m.id ? ' brf-rp-method--active' : ''}`}
                        onClick={() => { setPaymentMethod(m.id); setPaymentDetails(''); }}
                      >
                        <span className="brf-rp-method-label">{m.label}</span>
                        <span className="brf-rp-method-icons">{m.icons.map((ic, i) => <span key={i}>{ic}</span>)}</span>
                      </button>
                    ))}
                  </div>

                  {/* Right: form */}
                  <div className="brf-rp-right">
                    {paymentMethod === 'card' && (
                      <>
                        <div className="brf-rp-form-title">Add a new card</div>
                        <input className="brf-rp-input" placeholder="Card Number" maxLength={19}
                          value={paymentDetails} onChange={e => setPaymentDetails(e.target.value)} />
                        <div className="brf-rp-row">
                          <input className="brf-rp-input" placeholder="MM / YY" maxLength={5} />
                          <input className="brf-rp-input" placeholder="CVV" maxLength={4} type="password" />
                        </div>
                        <label className="brf-rp-check">
                          <input type="checkbox" /> Save this card as per RBI guidelines
                        </label>
                      </>
                    )}
                    {paymentMethod === 'upi' && (
                      <>
                        <div className="brf-rp-form-title">Pay via UPI</div>
                        <input className="brf-rp-input" placeholder="Enter UPI ID (e.g. name@upi)"
                          value={paymentDetails} onChange={e => setPaymentDetails(e.target.value)} />
                        <p className="brf-rp-hint">You will receive a payment request on your UPI app.</p>
                      </>
                    )}
                    {paymentMethod === 'netbanking' && (
                      <>
                        <div className="brf-rp-form-title">Select your Bank</div>
                        <div className="brf-nb-search-wrap">
                          <span className="brf-nb-search-icon">Search</span>
                          <input
                            className="brf-rp-input brf-nb-search-input"
                            placeholder="Search for Banks"
                            value={bankSearch}
                            onChange={e => setBankSearch(e.target.value)}
                          />
                        </div>
                        <div className="brf-nb-label">Suggested Banks</div>
                        <div className="brf-nb-list">
                          {['SBI','HDFC','ICICI','Axis','Kotak','Yes Bank','PNB','Bank of Baroda','Canara Bank','Union Bank'].filter(b =>
                            b.toLowerCase().includes(bankSearch.toLowerCase())
                          ).map(b => (
                            <button
                              key={b}
                              type="button"
                              className={`brf-nb-item${paymentDetails === b ? ' brf-nb-item--active' : ''}`}
                              onClick={() => setPaymentDetails(b)}
                            >
                              <span className="brf-nb-bank-icon">BANK</span>
                              <span className="brf-nb-bank-name">{b}</span>
                              <span className="brf-nb-arrow">&gt;</span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    {paymentMethod === 'wallet' && (
                      <>
                        <div className="brf-rp-form-title">Select Wallet</div>
                        <div className="brf-rp-wallet-grid">
                          {['Paytm','PhonePe','Amazon Pay','Mobikwik'].map(w => (
                            <button key={w} type="button"
                              className={`brf-rp-wallet-btn${paymentDetails === w ? ' brf-rp-wallet-btn--active' : ''}`}
                              onClick={() => setPaymentDetails(w)}>
                              {w}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    <button
                      type="button"
                      className="brf-rp-continue-btn"
                      onClick={handlePaymentSubmit}
                      disabled={submitting || !paymentDetails}
                    >
                      {submitting ? 'Processing...' : `Pay Rs ${totalAmount.toFixed(2)} & Confirm`}
                    </button>
                    <div className="brf-rp-secured">Secured by <strong>UBSAAPP</strong></div>
                  </div>
                </div>
              )}

              {/* Cash payment info */}
              {paymentType === 'cash' && (
                <div className="brf-cash-info">
                  <span className="brf-cash-icon">CASH</span>
                  <div>
                    <div className="brf-cash-title">Pay at Venue</div>
                    <div className="brf-cash-desc">Please carry Rs {totalAmount.toFixed(2)} in cash and pay at the venue before your meeting starts.</div>
                  </div>
                </div>
              )}

              {paymentType === 'cash' && (
                <button
                  type="button"
                  className="book-flow-btn-primary book-flow-btn-submit"
                  onClick={handlePaymentSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Confirming...' : `Confirm & Pay Rs ${totalAmount.toFixed(2)} at Venue`}
                </button>
              )}
            </>
          )}

          {/* Step 6: Confirmation */}
          {currentStep === 6 && (
            <>
              <h2 className="book-flow-step-title">Confirmation</h2>
              <p className="book-flow-step-subtitle">Booking Summary</p>
              <div className="book-flow-confirmation">
                <p><strong>Booking ID:</strong> {bookingId || "-"}</p>
                <p><strong>Room Name:</strong> {room?.roomName}</p>
                <p><strong>Date & Time:</strong> {selectedDate?.toLocaleDateString()} {startTime} - {endTime}</p>
                <p><strong>Cleaning:</strong> {cleaningResponsibilityLabel} ({cleaningCharge > 0 ? `Rs ${cleaningCharge.toFixed(2)}` : "Rs 0"})</p>
                <p><strong>Total Paid Amount:</strong> Rs {totalAmount.toFixed(2)}</p>
                <p className="book-flow-status">Booking Status: Confirmed</p>
                <div className="book-flow-confirm-actions">
                  <button type="button" className="book-flow-btn-secondary" onClick={handleDownloadInvoicePdf}>
                    Download Invoice
                  </button>
                  <button
                    type="button"
                    className="book-flow-btn-primary"
                    onClick={() => navigate("/meetings/my-bookings")}
                  >
                    Go to My Bookings
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Navigation */}
          {currentStep < 5 && (
            <div className="book-flow-actions">
              <button type="button" className="book-flow-btn-secondary" onClick={handleBack} disabled={currentStep === 1}>
                Back
              </button>
              <button
                type="button"
                className="book-flow-btn-primary"
                onClick={handleNext}
                disabled={
                  (currentStep === 1 && !canProceedStep1) ||
                  (currentStep === 3 && !canProceedStep3)
                }
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirming Payment Popup */}
      {showConfirmingPopup && (
        <div className="brf-popup-overlay">
          <div className="brf-confirming-popup">
            <div className="brf-confirming-coin">o</div>
            <h3>Confirming Payment</h3>
            <p>This will only take a few seconds.</p>
            <div className="brf-rp-secured" style={{marginTop:'1rem'}}>Secured by <strong>UBSAAPP</strong></div>
          </div>
        </div>
      )}

      {/* Payment Success Popup */}
      {showSuccessPopup && (
        <div className="brf-popup-overlay">
          <div className="brf-success-popup">
            <p className="brf-success-redirect">Payment Successful</p>
            <div className="brf-success-check">OK</div>
            <div className="brf-success-card">
              <div className="brf-success-card-row">
                <span>UBSAAPP</span>
                <span>Rs {totalAmount.toFixed(2)}</span>
              </div>
              <div className="brf-success-card-meta">
                {new Date().toLocaleString('en-IN', {day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}
              </div>
              <div className="brf-success-card-meta">
                Netbanking &nbsp;|&nbsp; {paymentRef}
              </div>
            </div>
            <button
              type="button"
              className="brf-success-done-btn"
              onClick={() => { setShowSuccessPopup(false); setCurrentStep(6); }}
            >
              Done
            </button>
            <div className="brf-rp-secured" style={{marginTop:'0.75rem'}}>Secured by <strong>UBSAAPP</strong></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookRoomFlow;

